import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Moon,
  Sun,
  CircleDot,
  Orbit,
  Flame,
  RotateCcw,
} from 'lucide-react';

const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formatMonth = (date) =>
  date.toLocaleDateString('en', { month: 'long', year: 'numeric' });
const toKey = (date) => date.toISOString().slice(0, 10);

const MEANING_BY_TYPE = {
  'moon-phase': '',
  'solar-eclipse': '',
  'lunar-eclipse': '',
  season: '',
  'planet-ingress': '',
  'planet-station': '',
  festival: '',
};

const getMoonPhaseData = (date) => {
  const target = date ? new Date(date) : new Date();
  let year = target.getFullYear();
  let month = target.getMonth() + 1;
  const day = target.getDate();
  if (month < 3) {
    year -= 1;
    month += 12;
  }
  ++month;
  const c = 365.25 * year;
  const e = 30.6 * month;
  let jd = c + e + day - 694039.09;
  jd /= 29.5305882;
  const b = Math.round((jd - Math.trunc(jd)) * 8);
  const phaseMap = {
    0: { name: 'New Moon', glyph: '🌑' },
    1: { name: 'Waxing Crescent', glyph: '🌒' },
    2: { name: 'First Quarter', glyph: '🌓' },
    3: { name: 'Waxing Gibbous', glyph: '🌔' },
    4: { name: 'Full Moon', glyph: '🌕' },
    5: { name: 'Waning Gibbous', glyph: '🌖' },
    6: { name: 'Last Quarter', glyph: '🌗' },
    7: { name: 'Waning Crescent', glyph: '🌘' },
    8: { name: 'New Moon', glyph: '🌑' },
  };
  return phaseMap[b] || { name: 'Moon Phase', glyph: '🌑' };
};

const CalendarComponent = ({
  initialDate = new Date(),
  selectedDate: selectedDateProp,
  onDateSelect,
  journalMarks = {},
  showJournalMarkers: showJournalMarkersProp,
  onToggleJournalMarkers,
  hideDetails = false,
  showMoonPhase = true,
  className,
}) => {
  const [selectedDate, setSelectedDate] = useState(selectedDateProp || initialDate);
  const [viewDate, setViewDate] = useState(selectedDateProp || initialDate);
  const [eventsByDay, setEventsByDay] = useState({});
  const [showDetails, setShowDetails] = useState(!hideDetails);
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [internalShowJournalMarkers, setInternalShowJournalMarkers] = useState(true);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    if (selectedDateProp) {
      const next = new Date(selectedDateProp);
      setSelectedDate(next);
      setViewDate(new Date(next.getFullYear(), next.getMonth(), 1));
    }
  }, [selectedDateProp]);

  const showJournalMarkers =
    typeof showJournalMarkersProp === 'boolean'
      ? showJournalMarkersProp
      : internalShowJournalMarkers;

  const baseYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, i) => baseYear - 2 + i),
    [baseYear]
  );
  const months = useMemo(
    () => [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    []
  );

  const weeks = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const slots = [
      ...Array(firstDay).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
    ];
    while (slots.length % 7 !== 0) slots.push(null);

    const rows = [];
    for (let i = 0; i < slots.length; i += 7) rows.push(slots.slice(i, i + 7));
    return rows;
  }, [viewDate]);

  const isToday = (date) =>
    date && date.toDateString() === new Date().toDateString();
  const isSelected = (date) =>
    date && selectedDate && date.toDateString() === selectedDate.toDateString();

  const shiftMonth = (delta) => {
    setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  };

  const setMonth = (monthIndex) => {
    setViewDate((current) => new Date(current.getFullYear(), monthIndex, 1));
  };

  const setYear = (yearValue) => {
    setViewDate((current) => new Date(yearValue, current.getMonth(), 1));
  };

  const handleSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
    setShowDetails(!hideDetails);
    onDateSelect?.(date);
  };

  const handleMonthPick = (idx) => {
    setMonth(idx);
    setIsMonthOpen(false);
  };

  const handleYearPick = (yearValue) => {
    setYear(yearValue);
    setIsYearOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (monthRef.current && !monthRef.current.contains(event.target)) {
        setIsMonthOpen(false);
      }
      if (yearRef.current && !yearRef.current.contains(event.target)) {
        setIsYearOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load events for the current view year (built JSON from /public/calendar)
  useEffect(() => {
    const year = viewDate.getFullYear();
    const cache = CalendarComponent._cache || (CalendarComponent._cache = {});
    if (cache[year]) {
      setEventsByDay(cache[year]);
      return;
    }
    fetch(`/calendar/events-${year}.json`)
      .then((res) => res.json())
      .then((data) => {
        const map = data.reduce((acc, event) => {
          const key = event.start.slice(0, 10);
          acc[key] = acc[key] || [];
          acc[key].push(event);
          return acc;
        }, {});
        cache[year] = map;
        setEventsByDay(map);
      })
      .catch(() => setEventsByDay({}));
  }, [viewDate]);

  const eventsForSelected = useMemo(
    () => eventsByDay[toKey(selectedDate)] || [],
    [eventsByDay, selectedDate]
  );

  const classLayout = ['calendar-layout', showDetails ? 'has-detail' : 'no-detail']
    .filter(Boolean)
    .join(' ');

  const legend = [
    { label: 'Lunar', icon: Moon, className: 'legend-lunar', types: ['moon-phase', 'lunar-eclipse'] },
    { label: 'Solar', icon: Sun, className: 'legend-solar', types: ['solar-eclipse', 'season'] },
    { label: 'Eclipse', icon: CircleDot, className: 'legend-eclipse', types: ['solar-eclipse', 'lunar-eclipse'] },
    { label: 'Planetary', icon: Orbit, className: 'legend-planetary', types: ['planet-ingress', 'planet-station'] },
    { label: 'Festival', icon: Flame, className: 'legend-festival', types: ['festival'] },
    { label: 'Retrograde', icon: RotateCcw, className: 'legend-retro', types: ['planet-station'] },
  ];

  const eventsForDay = (date) => eventsByDay[toKey(date)] || [];

  const colorForEvent = (event) => {
    const map = {
      'moon-phase': 'var(--dot-lunar)',
      'lunar-eclipse': 'var(--dot-eclipse)',
      'solar-eclipse': 'var(--dot-eclipse)',
      season: 'var(--dot-solar)',
      'planet-ingress': 'var(--dot-planetary)',
      'planet-station': 'var(--dot-retro)',
      festival: 'var(--dot-festival)',
    };
    return map[event?.type] || 'var(--dot-default)';
  };

  const iconForEvent = (event) => {
    const map = {
      'moon-phase': Moon,
      'lunar-eclipse': CircleDot,
      'solar-eclipse': CircleDot,
      season: Sun,
      'planet-ingress': Orbit,
      'planet-station': RotateCcw,
      festival: Flame,
    };
    return map[event?.type] || CircleDot;
  };

  const moonPhaseData = showMoonPhase ? getMoonPhaseData(selectedDate) : null;
  const toggleJournalMarkers = () => {
    const next = !showJournalMarkers;
    if (onToggleJournalMarkers) {
      onToggleJournalMarkers(next);
    } else {
      setInternalShowJournalMarkers(next);
    }
  };

  const sectionClassName = ['calendar-page', className]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={sectionClassName}>
      <div className={classLayout}>
        <div className="calendar-card">
          <div className="calendar-card__header">
            <div className="calendar-header-title">
              <p className="calendar-kicker">Currently viewing</p>
              <h2>{formatMonth(viewDate)}</h2>
            </div>
            {showMoonPhase && moonPhaseData && (
              <div className="moon-phase-pill calendar-moon-phase">
                <span className="moon-phase-pill__icon" aria-hidden="true">
                  {moonPhaseData.glyph}
                </span>
                <span>{moonPhaseData.name}</span>
              </div>
            )}
            <div className="calendar-controls">
              <div className="calendar-actions">
              <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">
                &lt;
              </button>
              <button type="button" onClick={() => handleSelect(new Date())}>
                Today
              </button>
              <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month">
                &gt;
              </button>
            </div>
              <div className="calendar-pickers">
              <div className="calendar-select" ref={monthRef}>
                <button
                  type="button"
                  className="calendar-select__trigger"
                  onClick={() => {
                    setIsMonthOpen((open) => !open);
                    setIsYearOpen(false);
                  }}
                  aria-expanded={isMonthOpen}
                  aria-haspopup="listbox"
                >
                  <span>{months[viewDate.getMonth()]}</span>
                  <span className="calendar-select__caret">▾</span>
                </button>
                {isMonthOpen && (
                  <div className="calendar-select__menu" role="listbox">
                    {months.map((m, idx) => (
                      <button
                        type="button"
                        key={m}
                        className={[
                          'calendar-select__option',
                          viewDate.getMonth() === idx && 'is-active',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => handleMonthPick(idx)}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="calendar-select" ref={yearRef}>
                <button
                  type="button"
                  className="calendar-select__trigger"
                  onClick={() => {
                    setIsYearOpen((open) => !open);
                    setIsMonthOpen(false);
                  }}
                  aria-expanded={isYearOpen}
                  aria-haspopup="listbox"
                >
                  <span>{viewDate.getFullYear()}</span>
                  <span className="calendar-select__caret">▾</span>
                </button>
                {isYearOpen && (
                  <div className="calendar-select__menu" role="listbox">
                    {yearOptions.map((y) => (
                      <button
                        type="button"
                        key={y}
                        className={[
                          'calendar-select__option',
                          viewDate.getFullYear() === y && 'is-active',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => handleYearPick(y)}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              </div>
            </div>
          </div>

          <div className="calendar-legend">
            {legend.map(({ label, icon: Icon, className }) => (
              <span key={label} className={`legend-item ${className}`}>
                <Icon size={16} />
                {label}
              </span>
            ))}
            <button
              type="button"
              className={`legend-item journal-toggle ${showJournalMarkers ? 'is-active' : ''}`}
              onClick={toggleJournalMarkers}
            >
              <CircleDot size={16} />
              Journal markers
            </button>
          </div>

          <div className="calendar-grid">
            {dayLabels.map((day) => (
              <div key={day} className="calendar-grid__label">
                {day}
              </div>
            ))}

            {weeks.map((week, index) => (
              <React.Fragment key={index}>
                {week.map((date, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={[
                      'calendar-day',
                      !date && 'is-empty',
                      date && isToday(date) && 'is-today',
                      date && isSelected(date) && 'is-selected',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => date && handleSelect(date)}
                    disabled={!date}
                  >
                    {date ? (
                      <>
                        <span>{date.getDate()}</span>
                        {eventsForDay(date).length > 0 && (
                          <div className="icon-stack" aria-label="Has events">
                            {eventsForDay(date)
                              .slice(0, 3)
                              .map((evt) => {
                                const Icon = iconForEvent(evt);
                                const color = colorForEvent(evt);
                                return (
                                  <Icon
                                    key={evt.id}
                                    size={14}
                                    className="day-icon"
                                    style={{ color }}
                                  />
                                );
                              })}
                            {eventsForDay(date).length > 3 && (
                              <span className="day-icon more">+{eventsForDay(date).length - 3}</span>
                            )}
                          </div>
                        )}
                        {showJournalMarkers && journalMarks[toKey(date)] && (
                          <span className="journal-dot">
                            {journalMarks[toKey(date)] > 1 ? journalMarks[toKey(date)] : ''}
                          </span>
                        )}
                      </>
                    ) : (
                      ''
                    )}
                  </button>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>

        <AnimatePresence>
          {!hideDetails && showDetails && selectedDate && (
            <motion.div
              key="sidebar"
              initial={{ opacity: 0, x: 30, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="calendar-card"
            >
              <div className="calendar-card__header">
                <div>
                  <p className="calendar-kicker">Selected date</p>
                  <h2>{selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</h2>
                </div>
                <button
                  type="button"
                  className="close-detail"
                  onClick={() => {
                    setShowDetails(false);
                  }}
                  aria-label="Close details"
                >
                  X
                </button>
              </div>

              <div className="calendar-side">
                <div className="calendar-note">
                  {eventsForSelected.length === 0 ? (
                    <p>No events for this date yet.</p>
                  ) : (
                    <ul className="calendar-event-list">
                      {eventsForSelected.map((event) => {
                        const color = colorForEvent(event);
                        return (
                          <li key={event.id}>
                            <span className="pill colored-pill" style={{ color }}>
                              {event.type}
                            </span>
                            <span className="event-title">{event.title}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default CalendarComponent;
